import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import WalletSelector from '../common/WalletSelector';
import ThemeToggle from '../common/ThemeToggle';
import SafeBalanceDisplay from '../common/SafeBalanceDisplay';
import './Header.css';

/**
 * App-wide Header Component
 * Provides navigation, user controls, and quick actions
 */
const Header = ({ currentView, setCurrentView, onMobileMenuToggle }) => {
  const { user, logout } = useAuth();
  const { activeWallet } = useWallet();
  const { theme } = useTheme();
  const { showSuccess, showError } = useToast();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const userMenuRef = useRef(null);
  const quickActionsRef = useRef(null);
  const notificationsRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target)) {
        setShowQuickActions(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationItems = [
    { id: 'solana-tokens', label: 'Top Tokens', icon: '⭐' },
    { id: 'trading', label: 'Trading', icon: '⚡' },
    { id: 'wallets', label: 'Wallets', icon: '💼' },
    { id: 'assets', label: 'Assets', icon: '💰' },
    { id: 'history', label: 'History', icon: '📈' }
  ];

  const quickActions = [
    { id: 'quick-trade', label: 'Quick Trade', icon: '⚡', action: () => setCurrentView('trading') },
    { id: 'new-wallet', label: 'New Wallet', icon: '➕', action: () => setCurrentView('wallets') },
    { id: 'price-alert', label: 'Price Alert', icon: '🔔', action: () => showSuccess('Price alert feature coming soon!') },
    { id: 'export-data', label: 'Export Data', icon: '📤', action: () => showSuccess('Export feature coming soon!') }
  ];

  const mockNotifications = [
    { id: 1, type: 'success', title: 'Trade Executed', message: 'SOL → USDC trade completed', time: '2 min ago', read: false },
    { id: 2, type: 'warning', title: 'Price Alert', message: 'SOL reached $150 target', time: '5 min ago', read: false },
    { id: 3, type: 'info', title: 'Wallet Connected', message: 'New wallet added successfully', time: '1 hour ago', read: true }
  ];

  const unreadNotifications = mockNotifications.filter(n => !n.read).length;

  const handleLogout = async () => {
    try {
      await logout();
      showSuccess('Logged out successfully');
    } catch (error) {
      showError('Failed to logout');
    }
    setShowUserMenu(false);
  };

  const handleNavigation = (viewId) => {
    setCurrentView(viewId);
  };

  const getPageTitle = () => {
    const titles = {
      dashboard: 'Dashboard',
      trading: 'Trading',
      'compact-trading': 'Enhanced Trading',
      wallets: 'Wallet Management',
      assets: 'My Assets',
      tokens: 'Token Watchlist',
      history: 'Trading History',
      discover: 'Token Discovery',
      monitor: 'Price Monitor',
      settings: 'Settings'
    };
    return titles[currentView] || 'Solana Trading Bot';
  };

  return (
    <header className="app-header">
      <div className="header-container">
        {/* Left Section - Logo & Navigation */}
        <div className="header-left">
          {/* <div className="header-logo">
            <div className="logo-icon">🚀</div>
            <div className="logo-text">
              <span className="logo-title">SolanaBot</span>
              <span className="logo-subtitle">Trading Platform</span>
            </div>
          </div> */}

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={onMobileMenuToggle}
            title="Toggle mobile menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Desktop Navigation */}
          <nav className="header-nav desktop-only">
            {navigationItems.map(item => (
              <button
                key={item.id}
                className={`nav-item ${currentView === item.id ? 'active' : ''}`}
                onClick={() => handleNavigation(item.id)}
                title={item.label}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Center Section - Page Title & Breadcrumb */}
        {/* <div className="header-center">
          <div className="page-title">
            <h1>{getPageTitle()}</h1>
            {activeWallet && (
              <div className="breadcrumb">
                <span className="wallet-indicator">
                  <span className="wallet-icon">💼88</span>
                  <span className="wallet-name">{activeWallet.name}</span>
                </span>
              </div>
            )}
          </div>
        </div> */}

        {/* Right Section - Controls & User Menu */}
        <div className="header-right">
          {/* Wallet Selector */}
          <div className="header-control">
            <WalletSelector compact={true} />
          </div>
          {/* Global SOL Balance */}
          <div className="header-control">
            <SafeBalanceDisplay compact={true} className="header-balance" />
          </div>

          {/* Quick Actions */}
          <div className="header-control" ref={quickActionsRef}>
            <button
              className="control-button quick-actions-btn"
              onClick={() => setShowQuickActions(!showQuickActions)}
              title="Quick Actions"
            >
              <span className="control-icon">⚡</span>
            </button>

            {showQuickActions && (
              <div className="dropdown-menu quick-actions-menu">
                <div className="dropdown-header">
                  <span>Quick Actions</span>
                </div>
                <div className="dropdown-content">
                  {quickActions.map(action => (
                    <button
                      key={action.id}
                      className="dropdown-item"
                      onClick={() => {
                        action.action();
                        setShowQuickActions(false);
                      }}
                    >
                      <span className="item-icon">{action.icon}</span>
                      <span className="item-label">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="header-control" ref={notificationsRef}>
            <button
              className="control-button notifications-btn"
              onClick={() => setShowNotifications(!showNotifications)}
              title="Notifications"
            >
              <span className="control-icon">🔔</span>
              {unreadNotifications > 0 && (
                <span className="notification-badge">{unreadNotifications}</span>
              )}
            </button>

            {showNotifications && (
              <div className="dropdown-menu notifications-menu">
                <div className="dropdown-header">
                  <span>Notifications</span>
                  <button className="mark-all-read">Mark all read</button>
                </div>
                <div className="dropdown-content">
                  {mockNotifications.length > 0 ? (
                    mockNotifications.map(notification => (
                      <div
                        key={notification.id}
                        className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                      >
                        <div className={`notification-type ${notification.type}`}>
                          {notification.type === 'success' && '✅'}
                          {notification.type === 'warning' && '⚠️'}
                          {notification.type === 'info' && 'ℹ️'}
                        </div>
                        <div className="notification-content">
                          <div className="notification-title">{notification.title}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-time">{notification.time}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-notifications">
                      <span>No notifications</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          {/* <div className="header-control">
            <ThemeToggle compact={true} />
          </div> */}

          {/* User Menu */}
          <div className="header-control" ref={userMenuRef}>
            <button
              className="control-button user-menu-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
              title="User Menu"
            >
              <div className="user-avatar">
                <span className="avatar-text">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              {/* <span className="user-name desktop-only">{user?.username}</span> */}
              <span className="dropdown-arrow">▼</span>
            </button>

            {showUserMenu && (
              <div className="dropdown-menu user-menu">
                <div className="dropdown-header">
                  <div className="user-info">
                    <div className="user-avatar large">
                      <span className="avatar-text">
                        {user?.username?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="user-details">
                      <div className="user-name">{user?.username}</div>
                      <div className="user-email">{user?.email}</div>
                    </div>
                  </div>
                </div>
                <div className="dropdown-content">
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      handleNavigation('settings');
                      setShowUserMenu(false);
                    }}
                  >
                    <span className="item-icon">⚙️</span>
                    <span className="item-label">Settings</span>
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      showSuccess('Profile feature coming soon!');
                      setShowUserMenu(false);
                    }}
                  >
                    <span className="item-icon">👤</span>
                    <span className="item-label">Profile</span>
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      showSuccess('Help center coming soon!');
                      setShowUserMenu(false);
                    }}
                  >
                    <span className="item-icon">❓</span>
                    <span className="item-label">Help</span>
                  </button>

                  <div className="header-control" style={{ marginLeft: 28 }}>
                    <ThemeToggle compact={true} />
                  </div>


                  <div className="dropdown-divider"></div>
                  <button
                    className="dropdown-item danger"
                    onClick={handleLogout}
                  >
                    <span className="item-icon">🚪</span>
                    <span className="item-label">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;